<template>
  <dialog ref="dlg">
    <form class="max-w-[420px] p-4 flex flex-col gap-3 bg-beigeClair" @submit.prevent="submit">
      <div class="flex items-center gap-4 mb-2">
        <h3 class="font-semibold text-lg bg-ocreClair py-2 px-4 rounded text-center">
          {{ isEdit ? 'Modifier l’inscription' : 'Inscription' }} — {{ course?.creneau_type?.titre }}
        </h3>
        <button class="ml-auto rounded p-1 text-sm btn" @click="close" aria-label="Fermer">
          <font-awesome :icon="['fas', 'xmark']" />
        </button>
      </div>
      <p class="opacity-80 text-lg">{{ dateLabel }}</p>

      <label class="text-sm">
        Nom *
        <input v-model="nom" required class="border rounded p-2 w-full" />
      </label>

      <label class="text-sm">
        Téléphone *
        <input
          v-model="telephone"
          required
          class="border rounded p-2 w-full"
          :class="{ 'border-red-600': telephone && !isPhoneValid }"
        />
        <span v-if="telephone" class="text-xs" :class="isPhoneValid ? 'text-gray-500' : 'text-red-600'">
          {{ hint }}
        </span>
      </label>

      <label class="text-sm">
        Email
        <input v-model="email" type="email" class="border rounded p-2 w-full" />
      </label>

      <p v-if="error" class="text-sm text-red-600 whitespace-pre-wrap">{{ error }}</p>
      <p v-if="success" class="text-sm text-green-700">{{ success }}</p>

      <div class="flex gap-2 justify-end w-full mt-4">
        <!-- <button type="button" class="btn" @click="close">Annuler</button> -->
        <button type="submit" class="btn bg-ocreMedium w-full py-2" :disabled="loading">
          {{ loading ? (isEdit ? 'Mise à jour…' : 'Envoi…') : isEdit ? 'Mettre à jour' : 'Valider' }}
        </button>
      </div>
    </form>
  </dialog>
</template>

<script setup lang="ts">
import type { CreneauInstance, Inscription } from '~/types/directus';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const props = defineProps<{
  course: CreneauInstance | null;
  inscription?: Inscription | null;
  adminOverride?: boolean;
}>();

const emit = defineEmits<{ (e: 'done', p: { status: 'confirmed' | 'already' | 'full' }): void }>();

const dlg = ref<HTMLDialogElement | null>(null);

const nom = ref<string>('');
const telephone = ref<string>('');
const email = ref<string>('');
const loading = ref<boolean>(false);
const error = ref<string>('');
const success = ref<string>('');
const config = useRuntimeConfig();

const isEdit = computed(() => !!props.inscription?.id);

const dateLabel = computed<string>(() => {
  if (!props.course) return '';
  const tz = (config.public.tz as string) || 'Europe/Paris';
  const start = new Date(props.course.start_datetime);
  const fmt = new Intl.DateTimeFormat('fr-FR', {
    timeZone: tz,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  return fmt.format(start);
});

function reset() {
  nom.value = '';
  telephone.value = '';
  email.value = '';
  error.value = '';
  success.value = '';
  loading.value = false;
}
// watch if props.inscription changes to prefill
watch(
  () => props.inscription,
  (newVal) => {
    if (newVal) {
      nom.value = [newVal.eleve?.nom].filter(Boolean).join(' ').trim();
      telephone.value = newVal.eleve?.telephone ?? '';
      email.value = newVal.eleve?.email ?? '';
    } else {
      reset();
    }
  },
  { immediate: true },
);

function open() {
  // Pré-remplissage si édition
  error.value = '';
  success.value = '';
  dlg.value?.showModal?.();
}
function close() {
  dlg.value?.close?.();
}

async function submit() {
  if (loading.value) return;

  loading.value = true;
  error.value = '';
  success.value = '';

  try {
    const formatted = validateAndFormatPhone(telephone.value);
    if (!formatted) {
      error.value = 'Téléphone invalide.';
      return;
    }

    if (isEdit.value && props.inscription?.id) {
      // --- EDITION : PATCH /api/inscriptions/update via useFetch
      const { data: res, error: fe } = await useFetch<{ ok: boolean; status?: string; message?: string }>(
        '/api/inscriptions/signup',
        {
          method: 'PATCH',
          body: {
            id: props.inscription.id,
            // champs édités côté prof
            eleve: {
              nom: nom.value.trim(),
              telephone: telephone.value.trim(), // ou formatted si tu veux forcer E.164
              email: email.value?.trim() || null,
            },
          },
        },
      );

      if (fe.value) throw fe.value;
      if (res.value?.ok) {
        success.value = 'Inscription mise à jour ✔';
        emit('done', { status: 'confirmed' });
        setTimeout(close, 600);
      } else {
        error.value = res.value?.message || 'Mise à jour impossible.';
      }
    } else {
      // --- CREATION : POST /api/inscriptions/signup via useFetch
      const { data: res, error: fe } = await useFetch<{
        ok: boolean;
        status: 'confirmed' | 'already' | 'full';
        message?: string;
      }>('/api/inscriptions/signup', {
        method: 'POST',
        body: {
          instanceId: props.course?.id,
          nom: nom.value.trim(),
          telephone: telephone.value.trim(), // ou formatted
          email: email.value?.trim() || null,
          adminOverride: props.adminOverride === true,
        },
      });

      if (fe.value) throw fe.value;

      if (res.value?.ok && res.value.status === 'confirmed') {
        success.value = 'Inscription confirmée ✔';
        emit('done', { status: 'confirmed' });
        setTimeout(close, 600);
      } else if (res.value?.status === 'already') {
        success.value = 'Déjà inscrit.';
        emit('done', { status: 'already' });
        setTimeout(close, 600);
      } else if (res.value?.status === 'full') {
        error.value = 'Ce créneau est complet.';
        emit('done', { status: 'full' });
      } else {
        error.value = res.value?.message || 'Une erreur est survenue.';
      }
    }
  } catch (e: any) {
    error.value = e?.data?.statusMessage || e?.message || 'Erreur réseau';
  } finally {
    loading.value = false;
  }
}

defineExpose({ open, close, reset });

// --- Phone helpers
function validateAndFormatPhone(raw: string) {
  const guessDefault = raw.trim().startsWith('+') ? undefined : 'FR';
  const pn = parsePhoneNumberFromString(raw, guessDefault);
  if (!pn || !pn.isValid()) return null;
  return pn.number; // E.164 si tu veux l'enregistrement strict
}

const hint = computed(() => {
  if (!telephone.value) return '';
  const defCountry = telephone.value.startsWith('+') ? undefined : 'FR';
  const pn = parsePhoneNumberFromString(telephone.value, defCountry);
  if (!pn) return 'Numéro invalide';
  if (!pn.isValid()) return 'Numéro incomplet ou incorrect';
  return ''; // pas de hint particulier si valide
});
const isPhoneValid = computed(() => {
  if (!telephone.value) return true;
  const defCountry = telephone.value.startsWith('+') ? undefined : 'FR';
  const pn = parsePhoneNumberFromString(telephone.value, defCountry);
  return pn?.isValid() ?? false;
});
</script>

<style scoped>
dialog::backdrop {
  background: rgba(0, 0, 0, 0.3);
}
dialog {
  border: none;
  border-radius: 12px;
}
label {
  display: grid;
  gap: 6px;
}
</style>
